import { useMemo } from 'react'
import { resolveClockItemTypes } from './showClock'

export type TitleSourceItem = {
  id: number | string
  item_type: string
  title?: string | null
}

type ItemTypeMap = Record<string, { key: string; label: string }>

export function buildItemTypeMap(): ItemTypeMap {
  // Derive a simple {typeKey: {key,label}} map once per render tree.
  const types = resolveClockItemTypes()
  const map: ItemTypeMap = {}
  for (const t of types) {
    map[t.key] = { key: t.key, label: t.label }
  }
  return map
}

/**
 * Pure helper to derive per-item display titles based on type sequence.
 */
export function computeDisplayTitles(
  ordered: TitleSourceItem[],
  itemTypeMap: ItemTypeMap,
): Record<string | number, string> {
  const counts: Record<string, number> = {}
  const out: Record<string | number, string> = {}

  for (const it of ordered) {
    const raw = (it.title ?? '').trim()
    if (raw) {
      out[it.id] = raw
      continue
    }
    const typeKey = it.item_type
    const meta = itemTypeMap[typeKey]
    const label = meta?.label ?? typeKey
    const n = (counts[typeKey] = (counts[typeKey] ?? 0) + 1)
    out[it.id] = `${label} ${n}`
  }
  return out
}

/**
 * React-friendly memoized variant for components.
 */
export function useDisplayTitles(ordered: TitleSourceItem[]) {
  // IMPORTANT: build the type map before computing titles to avoid TDZ issues.
  const itemTypeMap = useMemo(buildItemTypeMap, [])
  return useMemo(() => computeDisplayTitles(ordered, itemTypeMap), [ordered, itemTypeMap])
}

